import React, { Component } from "react";
import { Grid, Modal } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { UpsertVatDto, VatDto, VatSearchRequest } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { deleteVatGroup, findVatGroupById, searchVatGroups } from 'services/vatGroupServices';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertVatGroupForm from 'components/medicaments/UpsertVatGroupForm';
import { noop } from 'utils/functionUtils';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialVatGroupSearch: Partial<VatSearchRequest> = {};

interface VatGroupsFieldProps extends FormState {
  values: Partial<VatSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundVatGroups: Array<VatDto>,
  vatGroupsLoaded: boolean,
  vatGroup?: UpsertVatDto,
  vatGroupId?: number,
  addEditModalOpen: boolean,
  searchValue: Partial<VatSearchRequest>,
  cancelTokenSource: CancelTokenSource,
}

class VatGroupsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundVatGroups: [],
      vatGroupsLoaded: false,
      vatGroup: undefined,
      vatGroupId: undefined,
      addEditModalOpen: false,
      searchValue: initialVatGroupSearch,
      cancelTokenSource
    };

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findVatGroupById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, vatGroup?: UpsertVatDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        vatGroup: vatGroup,
        vatGroupId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      vatGroupId: undefined,
      vatGroup: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValue, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        vatGroupsLoaded: true
      })
    };

    searchVatGroups(searchValue, cancelTokenSource)
        .then(response => {
          this.setState({
            foundVatGroups: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteVatGroup = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteVatGroup(id, cancelTokenSource).then(response => {
      this.setState(({foundVatGroups}: State) => ({
        foundVatGroups: foundVatGroups.filter((medicament: VatDto) => medicament.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: VatGroupsFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    const {cancelTokenSource} = this.state;

    this.setState({vatGroupsLoaded: false});

    const onFinally = () => {
      this.setState({
        vatGroupsLoaded: true
      })
    };

    searchVatGroups(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundVatGroups: response,
            searchValue: values
          });
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {vatGroupsLoaded} = this.state;
    const {getMessage} = this.props;

    const vatGroupsView = (
        <PraxStyledSegment>
          {this.renderSearchVatGroupsSegment()}

          <StyledViewTableRow>
            {!vatGroupsLoaded
                ? <LoaderComponent message={getMessage("vat.groups.loading")}/>
                : this.renderVatGroupsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertVatGroupModal()}
        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {vatGroupsView}
        </React.Fragment>
    );
  }

  renderSearchVatGroupsSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderVatGroupsSearchForm}
        />
    );
  };

  renderVatGroupsSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundVatGroups} = this.state;

    const numResults = foundVatGroups.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("vat.group.search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="vatSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("vat.groups.confirmDelete")}
              onConfirm={() => this.deleteVatGroup(cellData)}
          />
        </React.Fragment>
    );
  };

  typeCellRenderer = ({cellData}: any) => {

    const {getMessage} = this.props;

    if (cellData === 0) {
      return `${getMessage("vat.group.type.incl")}`;
    } else {
      return `${getMessage("vat.group.type.excl")}`;
    }

  };

  valueCellRenderer = ({cellData}: any) => {
    return Number(cellData).toFixed(2);
  };

  vatGroupRowGetter = ({index}: any) => {
    const {foundVatGroups} = this.state;

    Object.assign(foundVatGroups[index], {index: index + 1});

    return foundVatGroups[index];
  };

  vatGroupRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundVatGroups} = this.state;

    const vatGroupId = foundVatGroups[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(vatGroupId)}
        >
          {columns}
        </div>
    );
  };


  renderVatGroupsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundVatGroups} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundVatGroups.length}
            rowGetter={this.vatGroupRowGetter}
            rowRenderer={this.vatGroupRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 2,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 100,
                flexGrow: 1,
                label: (getMessage("vat.group.type")),
                dataKey: "type",
                cellRenderer: (this.typeCellRenderer)
              },
              {
                width: 200,
                flexGrow: 1,
                label: (`${getMessage("value")} in %`),
                dataKey: "value",
                cellRenderer: (this.valueCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderUpsertVatGroupModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, vatGroup, vatGroupId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("vat.groups.header")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertVatGroupForm
                vatGroup={vatGroup}
                vatGroupId={vatGroupId}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

export default withMessages(VatGroupsView);
